/**
 * Clubhouse big-screen mode.
 *
 * Designed to be cast to a TV and left alone: fullscreen, no chrome, auto-rotating
 * between boards, pages of 10 so a 40-player field cycles legibly, and a
 * full-bleed sponsor card between rotations. Clubhouse screens are prime sponsor
 * real estate, so ad rotation is built in rather than bolted on.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { LeaderboardPayload } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { BigScreenAd, SponsorTicker } from '../components/AdZone.tsx';
import { Leaderboard } from '../components/Leaderboard.tsx';
import { Logo, Spinner } from '../components/Shell.tsx';
import { useFetch, useLive } from '../lib/useLive.ts';

const ROWS_PER_PAGE = 10;

interface DemoSummary {
  tournamentName: string;
  clubName: string;
  presentedBy: string;
  roundNumber: number;
  roundCount: number;
  coursePar: number;
  boards: Array<{ slug: string; name: string; bigscreenDwellSeconds: number }>;
  tickSeconds: number;
}

/** Demo variant: /demo/bigscreen. The live variant takes a tournament slug. */
export function BigScreenPage({ demo = false }: { demo?: boolean }) {
  const params = useParams<{ slug?: string }>();
  const { data: summary } = useFetch<DemoSummary>(demo ? '/api/demo' : null);

  if (demo && !summary) return <Spinner label="Preparing the clubhouse display" />;

  return (
    <AdProvider surface="bigscreen" tournamentId={demo ? 'demo-tournament' : (params.slug ?? null)} isDemo={demo} allowInterstitial={false}>
      {demo && summary ? <BigScreenBody summary={summary} demo /> : <LiveBigScreen slug={params.slug ?? ''} />}
    </AdProvider>
  );
}

function LiveBigScreen({ slug }: { slug: string }) {
  const { data, loading, error } = useFetch<{
    tournament: { id: string; name: string; presentedBy: string | null };
    pages: Array<{ dwellSeconds: number; payload: LeaderboardPayload }>;
    adRotateSeconds: number;
  }>(slug ? `/api/tournaments/${slug}/bigscreen` : null);

  if (loading) return <Spinner label="Loading display" />;
  if (error || !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--color-fairway-950)] p-10 text-center">
        <div>
          <Logo />
          <p className="mt-4 max-w-[46ch] text-sm text-[var(--color-muted)]">
            {error ?? 'This event is not available.'} The clubhouse display needs a live event; try the demo display at
            /demo/bigscreen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <BigScreenBody
      summary={{
        tournamentName: data.tournament.name,
        clubName: '',
        presentedBy: data.tournament.presentedBy ?? '',
        roundNumber: data.pages[0]?.payload.roundNumber ?? 1,
        roundCount: data.pages[0]?.payload.roundCount ?? 1,
        coursePar: data.pages[0]?.payload.coursePar ?? 72,
        boards: data.pages.map((p) => ({
          slug: p.payload.leaderboardId,
          name: p.payload.leaderboardName,
          bigscreenDwellSeconds: p.dwellSeconds,
        })),
        tickSeconds: 20,
      }}
      staticPages={data.pages.map((p) => p.payload)}
    />
  );
}

function BigScreenBody({
  summary,
  demo = false,
  staticPages,
}: {
  summary: DemoSummary;
  demo?: boolean;
  staticPages?: LeaderboardPayload[];
}) {
  /** One piece of state so the rotation cycle is a single transition. */
  const [cycle, setCycle] = useState({ boardIndex: 0, page: 0, showAd: false });
  const { boardIndex, page, showAd } = cycle;

  const board = summary.boards[boardIndex];
  const live = useLive<LeaderboardPayload>({
    fetchPath: demo ? `/api/demo/leaderboard/${board?.slug ?? 'gross'}` : '',
    streamPath: demo ? `/api/demo/stream/${board?.slug ?? 'gross'}` : undefined,
    pollSeconds: summary.tickSeconds,
    enabled: demo,
  });

  const payload = demo ? live.data : (staticPages?.[boardIndex] ?? null);
  const pageCount = payload ? Math.max(1, Math.ceil(payload.rows.length / ROWS_PER_PAGE)) : 1;

  /**
   * Rotation: page through the current board, show one full-bleed sponsor card,
   * then move to the next board. Sponsors get a guaranteed slot in every cycle.
   */
  const advance = useCallback(() => {
    const boardCount = Math.max(1, summary.boards.length);
    setCycle((current) => {
      if (current.showAd) {
        return { boardIndex: (current.boardIndex + 1) % boardCount, page: 0, showAd: false };
      }
      const nextPage = current.page + 1;
      return nextPage >= pageCount
        ? { ...current, showAd: true }
        : { ...current, page: nextPage };
    });
  }, [pageCount, summary.boards.length]);

  const dwell = showAd ? 10 : (board?.bigscreenDwellSeconds ?? 18);
  useEffect(() => {
    const timer = window.setTimeout(advance, Math.max(5, dwell) * 1000);
    return () => window.clearTimeout(timer);
  }, [advance, dwell, page, showAd, boardIndex]);

  // Keyboard: F for fullscreen, space to advance — for whoever sets the TV up.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'f' || event.key === 'F') void document.documentElement.requestFullscreen?.().catch(() => undefined);
      if (event.key === ' ') {
        event.preventDefault();
        advance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance]);

  const visibleRows = useMemo(() => {
    if (!payload) return null;
    return { ...payload, rows: payload.rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE) };
  }, [page, payload]);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-fairway-950)]">
      <header className="flex items-center justify-between border-b border-[color-mix(in_oklab,var(--color-fairway-400)_25%,transparent)] px-8 py-5">
        <div>
          <p className="eyebrow text-[var(--color-gold-500)]">
            {summary.presentedBy ? `Presented by ${summary.presentedBy}` : 'Live scoring'}
            {demo ? ' · DEMO MODE — SAMPLE DATA' : ''}
          </p>
          <h1 className="board-title text-[40px] font-bold leading-none">{summary.tournamentName}</h1>
        </div>
        <div className="text-right">
          <p className="board-title text-2xl font-bold text-[var(--color-fairway-300)]">{board?.name}</p>
          <p className="tabular text-sm text-[var(--color-muted)]">
            Round {summary.roundNumber} of {summary.roundCount} · Par {summary.coursePar}
            {pageCount > 1 ? ` · page ${page + 1}/${pageCount}` : ''}
          </p>
        </div>
      </header>

      <main className="flex-1 overflow-hidden px-8 py-6">
        {showAd ? (
          <div className="h-full overflow-hidden rounded-[18px]">
            <BigScreenAd />
          </div>
        ) : visibleRows ? (
          <Leaderboard payload={visibleRows} display />
        ) : (
          <Spinner label="Waiting for scores" />
        )}
      </main>

      <SponsorTicker />

      <footer className="flex items-center justify-between px-8 py-3 text-xs text-[var(--color-muted-dim)]">
        <span className="flex items-center gap-3">
          <Logo compact />
          <span>Press F for fullscreen · space to advance</span>
        </span>
        <span>
          {demo ? 'Fictional field and sponsors' : 'Live'} · updated{' '}
          {live.updatedAt ? new Date(live.updatedAt).toLocaleTimeString('en-ZA') : new Date().toLocaleTimeString('en-ZA')}
        </span>
      </footer>
    </div>
  );
}
